import { ChangeDetectionStrategy, Component, computed, input, model } from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { FormsModule } from "@angular/forms";
import { S25RadioModule } from "../../../s25-radio/s25.radio.module";
import { PaymentRows } from "./payment-rows";
import { S25PricingUtil } from "../../s25.pricing.util";

@TypeManagerDecorator("s25-ng-payment-amount-field")
@Component({
    selector: "s25-ng-payment-amount-field",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25RadioModule],
    host: { "[class.inline]": "orientation() === 'inline'" },
    template: `
        @if (entry().type === "percent") {
            <p class="resolved">{{ resolvedLabel() }}</p>
        }

        <div class="type-row" role="radiogroup" [attr.aria-label]="'Amount type'">
            <s25-ng-radio
                [value]="entry().type"
                [radioValue]="'amount'"
                [name]="groupName()"
                [disabled]="disabled()"
                (valueChange)="setType('amount')"
                >Amount</s25-ng-radio
            >
            <s25-ng-radio
                [value]="entry().type"
                [radioValue]="'percent'"
                [name]="groupName()"
                [disabled]="disabled()"
                (valueChange)="setType('percent')"
                >Percentage</s25-ng-radio
            >
        </div>

        <div class="amount-row">
            @if (entry().type === "amount") {
                <span aria-hidden="true">$</span>
            }
            <input
                class="c-input"
                type="number"
                step="0.01"
                min="0"
                [max]="max()"
                [attr.aria-label]="entry().type === 'percent' ? 'Percentage of balance' : 'Payment amount'"
                [disabled]="disabled()"
                [ngModel]="entry().value"
                (ngModelChange)="setValue($event)"
            />
            @if (entry().type === "percent") {
                <span aria-hidden="true">%</span>
            }
        </div>

        @if (overMax()) {
            <p class="over-max" role="alert">Cannot exceed {{ maxLabel() }}</p>
        }
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 6px;
        }

        .type-row {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
        }

        :host(.inline) .type-row {
            flex-direction: row;
            align-items: center;
            gap: 12px;
        }

        .amount-row {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        :host(.inline) .amount-row {
            max-width: 14em;
        }

        .amount-row .c-input {
            width: 100%;
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;
        }

        .resolved {
            font-weight: 600;
        }

        .over-max {
            color: var(--color--text-danger);
            font-size: 0.8rem;
        }
    `,
})
export class S25PaymentAmountFieldComponent {
    readonly entry = model.required<PaymentRows.PaymentAmountEntry>();
    /** The outstanding balance a percentage is taken from, and the ceiling on a dollar amount. */
    readonly balance = input(0);
    readonly disabled = input(false);
    /** Distinguishes this field's radio pair from every other one on the page. */
    readonly groupName = input.required<string>();
    /** Stacked by default, because a table cell has no room to put the radios side by side. */
    readonly orientation = input<"stacked" | "inline">("stacked");

    protected readonly max = computed(() => (this.entry().type === "percent" ? 100 : this.balance()));
    // A figure nobody can edit - a reminder's logged amount - is reported, not complained about.
    protected readonly overMax = computed(() => !this.disabled() && this.entry().value > this.max());
    protected readonly maxLabel = computed(() =>
        this.entry().type === "percent" ? "100%" : S25PricingUtil.formatCurrency(this.balance()),
    );
    protected readonly resolvedLabel = computed(() =>
        S25PricingUtil.formatCurrency(PaymentRows.paymentAmountDollars(this.entry(), this.balance())),
    );

    protected setType(type: PaymentRows.PaymentAmountEntry["type"]) {
        // A percentage starts at the whole balance, matching what the dollar field defaulted to.
        this.entry.set({ type, value: type === "percent" ? 100 : this.balance() });
    }

    protected setValue(value: number) {
        this.entry.update((prev) => ({ ...prev, value: Number(value) || 0 }));
    }
}
